import React, { useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import Form from '../../../common/Form';
import Input from '../../../common/Form/Input';
import Radio from '../../../common/Form/Radio';
import Upload from '../../../common/Form/FileUpload';
import ButtonScogoPrimary from '../../../common/Buttons/ButtonScogoPrimary';
import ButtonScogoClosedOutlined from '../../../common/Buttons/ButtonScogoClosedOutlined';
import { closeModalAction } from '../../../actions/modal';
import { convertIntoPaisa, convertIntoRupee } from '../../../utils/utils';
import { actualValueForDropdown, validateNumberValue } from '../../../utils/common';
import { postImageUrl } from '../../../actions/users';
import { activateProject, getProjectPo } from '../../../actions/project';
import { chargeTypes, radioOptionsBearer, billingTypes } from '../AddProject/Form';
import Select from '../../../common/Form/Select';
import { useEffect } from 'react';
import { MilestoneType } from '../../Ticket/TicketController';

const formName = 'activateProjectForm';

export const ActivateProject = ({ project, hidePo = false, submitButtonText = 'Activate Project' }) => {
    const dispatch = useDispatch();
    const submitRef = useRef(null);
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const isProjectDetailsLoading = useSelector((state) => state.loading.isProjectDetailsLoading);
    const inviteFormImagesUrls = useSelector((state) => state.user.inviteFormImagesUrls);
    const [additionalPoCount, setAdditionalPoCount] = useState(0);
    const projectPo = useSelector((state) => state.project.projectPo);
    const hasRmaMilestone = project?.milestones?.some((milestone) => milestone.type === MilestoneType.RMA);

    useEffect(() => {
        if (project?.id && !hidePo) dispatch(getProjectPo({ projectId: project.id }));
    }, [dispatch, project?.id, hidePo]);

    const isProjectCategoryRmaOrHelpdesk = useMemo(() => {
        return ['RMA', 'HELPDESK'].includes(project?.category);
    }, [project?.category]);


    const methods = useForm({ defaultValues: {}, mode: 'all' });
    const { watch, setValue } = methods;

    useEffect(() => {
        if (project) {
            setValue('customer_cost', convertIntoRupee(project.customer_cost));
            setValue('SP_SERVICE', convertIntoRupee(project.payments?.SP_SERVICE));
            setValue('PSP_INCENTIVE', convertIntoRupee(project.payments?.PSP_INCENTIVE));
            setValue('CLUSTER_INCENTIVE', convertIntoRupee(project.payments?.CLUSTER_INCENTIVE));
            if (project.payments?.WAREHOUSE_OWNER_INCENTIVE) setValue('WAREHOUSE_OWNER_INCENTIVE', convertIntoRupee(project.payments?.WAREHOUSE_OWNER_INCENTIVE));
            setValue('payment_terms', validateNumberValue(project.payment_terms));
            setValue('per_ticket_budget_in_paisa', convertIntoRupee(project.per_ticket_budget_in_paisa));
            setValue('budget_in_paisa', convertIntoRupee(project.budget_in_paisa));

            if (Object.keys(project?.additional_cost_bearers).length > 0) {
                chargeTypes.forEach((charge) => {
                    setValue(`bearer_${charge.key}`, project?.additional_cost_bearers[charge.key]);
                });
            }

            if (isProjectCategoryRmaOrHelpdesk) {
                if (project?.max_tickets_allowed) {
                    setValue('max_tickets_allowed', project.max_tickets_allowed);
                }
                if (project?.fault_rate) {
                    setValue('fault_rate', project.fault_rate);
                }
                if (project?.upper_limit) {
                    setValue('upper_limit', project.upper_limit);
                }
            }

            if (project.period) {
                setValue('billing_type', { value: project.period, label: project.period, key: project.period });
            }
        };
    }, [project, setValue, isProjectCategoryRmaOrHelpdesk]);


    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    useEffect(() => {
        if (projectPo && projectPo.length > 0 && !hidePo) {
            setValue('purchase_orders', projectPo.map(({ amount, po_number, po_id, start_date, end_date }) => {
                const po = {
                    po_amount: convertIntoRupee(amount),
                    po_number,
                    po_id,
                    is_existing: true
                }
                if (start_date) po.start_date = formatDateForInput(start_date);
                if (end_date) po.end_date = formatDateForInput(end_date);
                return po;
            }));
            // Set additionalPoCount based on existing POs (subtract 1 because first PO is always shown)
            if (projectPo.length > 1) {
                setAdditionalPoCount(projectPo.length - 1);
            }
        }
    }, [projectPo, setValue, hidePo]);

    const customerPrice = watch('customer_cost') || 0;
    const vendorPrice = watch('SP_SERVICE') || 0;
    const clusterIncentive = watch('CLUSTER_INCENTIVE') || 0;
    const pspIncentive = watch('PSP_INCENTIVE') || 0;
    const warehouseOwnerIncentive = watch('WAREHOUSE_OWNER_INCENTIVE') || 0;

    const faultRateValue = watch('fault_rate');
    const numberOfTicketsValue = watch('max_tickets_allowed');

    const calculatedCost = useMemo(() => {
        return Number(vendorPrice) + Number(clusterIncentive) + Number(pspIncentive) + Number(warehouseOwnerIncentive);
    }, [vendorPrice, clusterIncentive, pspIncentive, warehouseOwnerIncentive]);

    const calculatedProfit = useMemo(() => {
        return Number(customerPrice) - calculatedCost;
    }, [customerPrice, calculatedCost]);

    const calculatedProfitPercentage = useMemo(() => {
        if (Number(customerPrice) === 0) return 0;
        return ((calculatedProfit / Number(customerPrice)) * 100).toFixed(2);
    }, [customerPrice, calculatedProfit]);

    const onCancel = () => {
        dispatch(closeModalAction());
    };

    const onFileUpload = (files, ref) => {
        let additionalData = {}
        if (project?.id) {
            additionalData = { project_id: project.id }
        } else {
            additionalData = { create: true }
        }
        let purpose
        if (ref.startsWith('purchase_orders')) {
            purpose = 'PROJECT_PO'
        }
        dispatch(postImageUrl({ files, type: 'kyc', ref, purpose: purpose, additionalData: additionalData }));
    }

    const onSubmit = (form) => {
        const activationData = {};

        let additional_cost_bearers = [];
        chargeTypes.forEach((charge) => {
            additional_cost_bearers.push({
                category: charge.key,
                bearer: form[`bearer_${charge.key}`]
            })
        });

        if (additional_cost_bearers.length > 0) {
            activationData.additional_cost_bearers = additional_cost_bearers;
        }

        if (form.per_ticket_budget_in_paisa) activationData.per_ticket_budget_in_paisa = convertIntoPaisa(form.per_ticket_budget_in_paisa);
        if (form.fault_rate) activationData.fault_rate = validateNumberValue(form.fault_rate);
        if (form.budget_in_paisa) activationData.budget_in_paisa = convertIntoPaisa(form.budget_in_paisa);
        if (form.max_tickets_allowed) activationData.max_tickets_allowed = validateNumberValue(form.max_tickets_allowed);
        if (form.billing_type) activationData.period = actualValueForDropdown(form.billing_type);
        if (form.upper_limit) activationData.upper_limit = validateNumberValue(form.upper_limit);

        // Handle purchase orders - only include new POs (without po_id)
        let purchase_orders = [];
        if (form.purchase_orders && Array.isArray(form.purchase_orders)) {
            form.purchase_orders.forEach((po, index) => {
                // Only process POs that is new added     
                if (!po.is_existing && (po.po_number || po.po_amount || po.po_file)) {
                    const currentFile = inviteFormImagesUrls[`purchase_orders[${index}].po_file`] ? inviteFormImagesUrls[`purchase_orders[${index}].po_file`] : '';
                    let additionalPO = { currency: 'INR' };
                    if (po.po_number) additionalPO.po_number = po.po_number;
                    if (po.po_amount) additionalPO.amount = convertIntoPaisa(po.po_amount);
                    if (currentFile) additionalPO.attachments = [currentFile];
                    if (po.start_date) additionalPO.start_date = po.start_date;
                    if (po.end_date) additionalPO.end_date = po.end_date;
                    purchase_orders.push(additionalPO);
                }
            });
        }

        if (purchase_orders.length > 0) {
            activationData.add_pos = purchase_orders;
        }

        if (form.customer_cost) activationData.customer_cost = convertIntoPaisa(form.customer_cost);
        let payments = {
            SP_SERVICE: convertIntoPaisa(form.SP_SERVICE),
            PSP_INCENTIVE: convertIntoPaisa(form.PSP_INCENTIVE),
            CLUSTER_INCENTIVE: convertIntoPaisa(form.CLUSTER_INCENTIVE),
        }
        if (Object.keys(payments).length > 0) activationData.payments = payments;
        if (form.payment_terms) activationData.payment_terms = validateNumberValue(form.payment_terms);
        if (hasRmaMilestone) payments.WAREHOUSE_OWNER_INCENTIVE = convertIntoPaisa(form.WAREHOUSE_OWNER_INCENTIVE);

        dispatch(activateProject({ id: project.id, payload: activationData, formName }));
    };

    return (
        <>
            <style>{`
                input[type="date"]::-webkit-calendar-picker-indicator {
                    cursor: pointer;
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    width: auto;
                    height: auto;
                    color: transparent;
                    background: transparent;
                }
                input[type="date"] {
                    position: relative;
                }
            `}</style>
            <Form
                methods={methods}
                className='overflow-hidden h-full relative'
                onSubmit={(e) => {
                    onSubmit(e);
                }}
                submitRef={submitRef}
            >
                {/* Loading Overlay */}
                {isProjectDetailsLoading && (
                    <div className='absolute inset-0 bg-white bg-opacity-70 z-50 flex items-center justify-center'>
                        <div className='flex flex-col items-center gap-3'>
                            <div className='animate-spin rounded-full h-12 w-12 border-b-4 border-scogoprimary'></div>
                            <p className='text-gray-600 font-semibold'>Loading project details...</p>
                        </div>
                    </div>
                )}

                <div className='flex-auto gap-4 overflow-y-auto bg-gray-50 p-4 h-[50vh]'>

                    {/* Incentives Card */}
                    <div className='bg-white rounded-xl shadow-lg p-6 mb-4 border border-gray-200 hover:shadow-xl transition-shadow duration-300'>
                        <div className='flex items-center gap-2 mb-4 pb-3 border-b border-gray-200'>
                            <div>
                                <h3 className="font-bold text-lg text-gray-800">Per Ticket Pricing & Incentives</h3>
                                <p className="text-xs text-gray-500">Configure pricing, payment terms, and team incentives</p>
                            </div>
                        </div>
                        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
                            <Input placeholder='₹ 0.00' label='Charges To Customer' type='number' name='customer_cost' required step='any' />
                            <Input placeholder='₹ 0.00' label='Vendor Price' name='SP_SERVICE' required type='number' step='any' />
                            <Input placeholder='₹ 0.00' label='Cluster Lead Incentive' name='CLUSTER_INCENTIVE' type='number' required step='any' />
                            <Input placeholder='₹ 0.00' label='PSP Incentive' name='PSP_INCENTIVE' type='number' required step='any' />
                            {hasRmaMilestone && <Input placeholder='₹ 0.00' label='Warehouse Incentive' name='WAREHOUSE_OWNER_INCENTIVE' type='number' required step='any' />}
                            <Input label='Payment Timeline' required name='payment_terms' type='number' />
                            <div className='bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-2 border-2 border-blue-300 shadow-sm'>
                                <div className='flex items-center gap-2 mb-2'>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                    <span className='text-sm font-medium text-gray-600'>Total Cost to SCOGO <span className='text-xs text-gray-500'>(Vendor + Incentives)</span></span>
                                </div>
                                <p className='text-2xl font-bold text-blue-700'>₹ {calculatedCost.toFixed(2)}</p>

                            </div>

                            <div className={`bg-gradient-to-br ${calculatedProfit >= 0 ? 'from-green-50 to-green-100 border-green-300' : 'from-red-50 to-red-100 border-red-300'} rounded-lg p-2 border-2 shadow-sm`}>
                                <div className='flex items-center gap-2 mb-2'>
                                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${calculatedProfit >= 0 ? 'text-green-600' : 'text-red-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className='text-sm font-medium text-gray-600'>Profit <span className='text-xs text-gray-500'>(Customer - Cost)</span></span>
                                </div>
                                <p className={`text-2xl font-bold ${calculatedProfit >= 0 ? 'text-green-700' : 'text-red-700'}`}>₹ {calculatedProfit.toFixed(2)}</p>
                            </div>

                            <div className={`bg-gradient-to-br ${calculatedProfit >= 0 ? 'from-purple-50 to-purple-100 border-purple-300' : 'from-orange-50 to-orange-100 border-orange-300'} rounded-lg p-2 border-2 shadow-sm`}>
                                <div className='flex items-center gap-2 mb-2'>
                                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${calculatedProfit >= 0 ? 'text-purple-600' : 'text-orange-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <span className='text-sm font-medium text-gray-600'>Profit % <span className='text-xs text-gray-500'>(Profit / Customer) × 100</span></span>
                                </div>
                                <p className={`text-2xl font-bold ${calculatedProfit >= 0 ? 'text-purple-700' : 'text-orange-700'}`}>{calculatedProfitPercentage}%</p>
                            </div>
                        </div>
                    </div>

                    {/* Budget Card */}
                    <div className='bg-white rounded-xl shadow-lg p-6 mb-4 border border-gray-200 hover:shadow-xl transition-shadow duration-300'>
                        <div className='flex items-center gap-2 mb-4 pb-3 border-b border-gray-200'>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-scogoprimary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <div>
                                <h3 className="font-bold text-lg text-gray-800">Budget Planning</h3>
                                <p className="text-xs text-gray-500">Set project budget, per-ticket allocation, and projections</p>
                            </div>
                        </div>
                        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
                            <div className='relative'>
                                <Input required label='Project Budget' type='number' name='budget_in_paisa' step='any' placeholder='₹ 0.00' />
                            </div>
                            <div className='relative'>
                                <Input required label='Per Ticket Budget' type='number' name='per_ticket_budget_in_paisa' step='any' placeholder='₹ 0.00' />
                            </div>
                            {/* <div className='relative'>
                            <Input label='Projection' name='projection' placeholder='Enter projection value' />
                        </div> */}
                            {
                                isProjectCategoryRmaOrHelpdesk ? (<>
                                    <div className='relative'>
                                        <Input
                                            required={!faultRateValue}
                                            label='Number of tickets'
                                            name='max_tickets_allowed'
                                            placeholder='Enter number of tickets'
                                            validation={{
                                                validate: (value) => {
                                                    if (!faultRateValue && !value) {
                                                        return 'Number of tickets is required when fault rate is not provided';
                                                    }
                                                    return true;
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className='relative'>
                                        <Input
                                            required={!numberOfTicketsValue}
                                            label='Fault Rate'
                                            name='fault_rate'
                                            placeholder='Enter fault rate value'
                                            validation={{
                                                validate: (value) => {
                                                    if (!numberOfTicketsValue && !value) {
                                                        return 'Fault rate is required when number of tickets is not provided';
                                                    }
                                                    return true;
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className='relative'>
                                        <Input
                                            required={!!faultRateValue}
                                            label='Total no. of assets under AMC'
                                            name='upper_limit'
                                            placeholder='Enter total no. of assets under AMC value'
                                            validation={{
                                                validate: (value) => {
                                                    if (faultRateValue && !value) {
                                                        return 'Total no. of assets under AMC is required when fault rate is provided';
                                                    }
                                                    return true;
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className='relative'>
                                        <Select label='Billing Type' name='billing_type' options={billingTypes} />
                                    </div>
                                </>) : null
                            }
                        </div>
                    </div>

                    {/* Additional Charges Bearer Card */}
                    <div className='bg-white rounded-xl shadow-lg p-6 mb-4 border border-gray-200 hover:shadow-xl transition-shadow duration-300'>
                        <div className='flex items-center gap-2 mb-4 pb-3 border-b border-gray-200'>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-scogoprimary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                            <div>
                                <h3 className="font-bold text-lg text-gray-800">Additional Charges Bearer</h3>
                                <p className="text-xs text-gray-500">Specify who bears the cost for each service type</p>
                            </div>
                        </div>
                        <div className='flex flex-wrap gap-4'>
                            {chargeTypes.map((charge) => (
                                <div key={charge.key} className='flex border-2 border-gray-300 rounded-lg p-4 bg-gradient-to-br from-gray-50 to-white hover:border-scogoprimary hover:shadow-md transition-all duration-200'>
                                    <div className='flex items-center gap-2'>
                                        {charge.key === 'SERVICE' && <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}
                                        {charge.key === 'TRAVEL' && <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>}
                                        {charge.key === 'MATERIAL' && <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>}
                                        {charge.key === 'COURIER' && <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>}
                                        {charge.key === 'INCENTIVE' && <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>}
                                        {charge.key === 'GST' && <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" /></svg>}
                                        {charge.key === 'OVERTIME' && <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                                        <span className='font-semibold text-sm text-gray-800'>{charge.label}</span>
                                    </div>
                                    <Radio
                                        className='flex gap-2'
                                        name={`bearer_${charge.key}`}
                                        id={`bearer_${charge.key}`}
                                        options={radioOptionsBearer.map(opt => ({ ...opt, name: `bearer_${charge.key}` }))}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* PO Details Card */}
                    {hidePo ? null : <div className='bg-white rounded-xl shadow-lg p-6 mb-4 border border-gray-200 hover:shadow-xl transition-shadow duration-300'>
                        <div className='flex items-center gap-2 mb-4 pb-3 border-b border-gray-200'>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-scogoprimary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <div>
                                <h3 className="font-bold text-lg text-gray-800">Purchase Order (PO) Details</h3>
                                <p className="text-xs text-gray-500">Manage purchase orders with dates and documentation</p>
                            </div>
                        </div>
                        <div className='flex flex-col gap-4 w-full'>
                            <div className='border-2 border-blue-300 rounded-lg p-5 bg-gradient-to-br from-blue-50 to-white relative'>
                                <div className='absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-md font-semibold'>
                                    PO #1
                                </div>
                                {watch('purchase_orders')?.[0]?.po_id && (
                                    <div className='absolute top-2 right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-md font-semibold'>
                                        Existing PO
                                    </div>
                                )}
                                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-6'>
                                    <Input className='w-full' readOnly={!!watch('purchase_orders')?.[0]?.po_id} label='PO Number' name='purchase_orders[0].po_number' placeholder='Enter PO number' />
                                    <Input className='w-full' readOnly={!!watch('purchase_orders')?.[0]?.po_id} label='PO Amount' name='purchase_orders[0].po_amount' type='number' step='any' placeholder='Enter amount' />
                                    <Input className='w-full' readOnly={!!watch('purchase_orders')?.[0]?.po_id} label='Start Date' name='purchase_orders[0].start_date' type='date' />
                                    <Input className='w-full' readOnly={!!watch('purchase_orders')?.[0]?.po_id} label='End Date' name='purchase_orders[0].end_date' type='date' />
                                    <Upload className='w-full'
                                        id='po_file_0'
                                        label='PO File'
                                        name='purchase_orders[0].po_file'
                                        onChange={e => onFileUpload(e, 'purchase_orders[0].po_file')}
                                        accept="image/jpeg,image/png,application/pdf"
                                        disabled={!!watch('purchase_orders')?.[0]?.po_id}
                                        validateFileType={file => {
                                            const isAcceptedFileType = ['image/jpeg', 'image/png', 'application/pdf'].includes(file.type);
                                            return isAcceptedFileType || `${file.name} is not an accepted file type`;
                                        }}
                                    />
                                </div>
                            </div>

                            {Array.from({ length: additionalPoCount }).map((_, index) => {
                                const currentPoIndex = index + 1;
                                const isExistingPo = !!watch('purchase_orders')?.[currentPoIndex]?.po_id;
                                return (
                                    <div key={currentPoIndex} className='border-2 border-gray-300 rounded-lg p-5 bg-gradient-to-br from-gray-50 to-white relative hover:border-scogoprimary transition-all duration-200'>
                                        <div className='absolute top-2 left-2 bg-gray-700 text-white text-xs px-2 py-1 rounded-md font-semibold'>
                                            PO #{currentPoIndex + 1}
                                        </div>
                                        {isExistingPo && (
                                            <div className='absolute top-2 left-20 bg-orange-500 text-white text-xs px-2 py-1 rounded-md font-semibold'>
                                                Existing PO
                                            </div>
                                        )}
                                        {!isExistingPo && (
                                            <button
                                                type="button"
                                                className="absolute top-2 right-2 text-red-500 hover:text-red-700 transition-colors p-2 rounded-full hover:bg-red-100"
                                                onClick={() => {
                                                    const newCount = additionalPoCount - 1;
                                                    setAdditionalPoCount(newCount >= 0 ? newCount : 0);
                                                }}
                                                title="Delete this PO"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        )}
                                        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-6'>
                                            <Input className='w-full' readOnly={isExistingPo} label='PO Number' name={`purchase_orders[${currentPoIndex}].po_number`} placeholder='Enter PO number' />
                                            <Input className='w-full' readOnly={isExistingPo} label='PO Amount' name={`purchase_orders[${currentPoIndex}].po_amount`} type='number' step='any' placeholder='Enter amount' />
                                            <Input className='w-full' readOnly={isExistingPo} label='Start Date' name={`purchase_orders[${currentPoIndex}].start_date`} type='date' />
                                            <Input className='w-full' readOnly={isExistingPo} label='End Date' name={`purchase_orders[${currentPoIndex}].end_date`} type='date' />
                                            <Upload className='w-full'
                                                id={`po_file_${currentPoIndex}`}
                                                label='PO File'
                                                name={`purchase_orders[${currentPoIndex}].po_file`}
                                                onChange={e => onFileUpload(e, `purchase_orders[${currentPoIndex}].po_file`)}
                                                accept="image/jpeg,image/png,application/pdf"
                                                disabled={isExistingPo}
                                                validateFileType={file => {
                                                    const isAcceptedFileType = ['image/jpeg', 'image/png', 'application/pdf'].includes(file.type);
                                                    return isAcceptedFileType || `${file.name} is not an accepted file type`;
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Add More PO button */}
                            <div className='flex justify-end pt-2'>
                                <button
                                    type="button"
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
                                    onClick={() => setAdditionalPoCount(prev => prev + 1)}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    <span className='text-sm'>Add More PO</span>
                                </button>
                            </div>
                        </div>
                    </div>}
                </div>

                {/* Action Buttons */}
                <div className='flex-none gap-4 h-[65px] bg-white border-t-2 border-gray-200 shadow-lg'>
                    <div className='flex gap-4 justify-end items-center h-full px-6'>
                        <ButtonScogoClosedOutlined
                            disabled={formButtonLoading[formName]}
                            textOrComponent='Cancel'
                            type='button'
                            onClick={onCancel}
                        />
                        <ButtonScogoPrimary
                            loading={formButtonLoading[formName]}
                            onClick={() => submitRef.current?.click()}
                            textOrComponent={submitButtonText}
                            type='submit'
                        />
                    </div>
                </div>
            </Form>
        </>
    );
};